'use client'

import { useRouter } from 'next/navigation';
import { usePathname } from 'next/navigation';
import { 
    getLanguageFromPath, 
    getLanguageData, 
    getEnabledLanguages,
    buildLanguagePath,
    localizePath,
    getTranslation,
    getFormTranslation,
    getNavigationData,
    getLanguageMetadata,
    getLanguageContactInfo,
    getLanguageCurrency
} from './utils';
import { siteConfig, type LanguageConfig } from './site-config';
import { getCurrentLanguage } from '@/lib/languages';

/**
 * Хук для работы с языками в компонентах
 */
export function useLanguage() {
    const pathname = usePathname() || '/';
    const router = useRouter();
    const currentLang = getCurrentLanguage(pathname);
    
    // Найдем конфигурацию для текущего языка
    const languageConfig = siteConfig.languages.find(lang => lang.code === currentLang.code);
    const currentLanguage = languageConfig || siteConfig.languages.find(lang => lang.isDefault) || siteConfig.languages[0];
    
    const currentLanguageData = getLanguageData(currentLanguage.code);
    const enabledLanguages = getEnabledLanguages();
    
    // Функция для переключения языка
    const switchLanguage = (langCode: string) => {
        const newPath = buildLanguagePath(langCode, pathname);
        router.push(newPath);
    };
    
    // Функция для получения перевода
    const t = (key: string, section?: string) => {
        return getTranslation(currentLanguage.code, key, section);
    };
    
    // Функция для получения переводов форм
    const tf = (type: 'labels' | 'buttons' | 'placeholders' | 'validation', key: string) => {
        return getFormTranslation(currentLanguage.code, type, key);
    };
    
    // Функция для получения навигационных данных
    const getNavigation = (section: 'main' | 'company' | 'bottomBar' | 'footer') => {
        return getNavigationData(currentLanguage.code, section);
    };
    
    // Получение мета-данных для текущего языка
    const metadata = getLanguageMetadata(currentLanguage.code);
    const contactInfo = getLanguageContactInfo(currentLanguage.code);
    const currency = getLanguageCurrency(currentLanguage.code);
    
    // Функция для локализации пути с текущим языком
    const localizePathForCurrentLang = (path: string) => {
        return localizePath(path, currentLanguage.code);
    };
    
    return {
        currentLanguage,
        currentLanguageData,
        enabledLanguages,
        switchLanguage,
        t,
        tf,
        getNavigation,
        metadata,
        contactInfo,
        currency,
        isRTL: currentLanguage.code === 'ar' || currentLanguage.code === 'he',
        langCode: currentLanguage.code,
        langPath: currentLanguage.path || '',
        localizePath: localizePathForCurrentLang
    };
}

/**
 * Простая версия хука без роутера для серверных компонентов
 */
export function useLanguageStatic(pathname: string) {
    const currentLanguage = getLanguageFromPath(pathname);
    const currentLanguageData = getLanguageData(currentLanguage.code);
    const enabledLanguages = getEnabledLanguages();
    
    // Функция для получения перевода
    const t = (key: string, section?: string) => {
        return getTranslation(currentLanguage.code, key, section);
    };
    
    // Функция для получения переводов форм
    const tf = (type: 'labels' | 'buttons' | 'placeholders' | 'validation', key: string) => {
        return getFormTranslation(currentLanguage.code, type, key);
    };
    
    // Функция для получения навигационных данных
    const getNavigation = (section: 'main' | 'company' | 'bottomBar' | 'footer') => {
        return getNavigationData(currentLanguage.code, section);
    };
    
    // Получение мета-данных для текущего языка
    const metadata = getLanguageMetadata(currentLanguage.code);
    const contactInfo = getLanguageContactInfo(currentLanguage.code);
    const currency = getLanguageCurrency(currentLanguage.code);
    
    // Функция для локализации пути с текущим языком
    const localizePathForCurrentLang = (path: string) => {
        return localizePath(path, currentLanguage.code);
    };
    
    return {
        currentLanguage,
        currentLanguageData,
        enabledLanguages,
        t,
        tf,
        getNavigation,
        metadata,
        contactInfo,
        currency,
        isRTL: currentLanguage.code === 'ar' || currentLanguage.code === 'he',
        langCode: currentLanguage.code,
        langPath: currentLanguage.path || '',
        localizePath: localizePathForCurrentLang
    };
}

/**
 * Компонент для автоматического переключения контента в зависимости от языка
 */
interface LanguageSwitcherProps {
    children: Record<string, React.ReactNode>;
    fallback?: React.ReactNode;
}

export function LanguageSwitcher({ children, fallback = null }: LanguageSwitcherProps) {
    const { langCode } = useLanguage();
    
    return <>{children[langCode] || fallback}</>;
}

/**
 * Компонент для отображения текста с автоматическим переводом
 */
interface TranslatedTextProps {
    keyName: string;
    section?: string;
    fallback?: string;
    values?: Record<string, string | number>;
}

export function TranslatedText({ keyName, section, fallback, values }: TranslatedTextProps) {
    const { t } = useLanguage();
    
    let text = t(keyName, section) || fallback || keyName;
    
    // Подстановка значений в шаблон (например: "Hello {name}" -> "Hello John")
    if (values) {
        Object.entries(values).forEach(([key, value]) => {
            text = text.replace(new RegExp(`\\{${key}\\}`, 'g'), String(value));
        });
    }
    
    return <>{text}</>;
}

export function useLanguageConfig() {
    const currentLanguage = useLanguage();
    
    return {
        currentLanguage,
        metadata: currentLanguage.metadata,
        contactInfo: currentLanguage.contactInfo,
        currency: currentLanguage.currency,
        features: siteConfig.features,
        general: siteConfig.general,
        getHomePath: () => currentLanguage.currentLanguage.path || '/',
        isFeatureEnabled: (feature: keyof typeof siteConfig.features) => {
            return siteConfig.features[feature].enabled;
        }
    };
}

export default useLanguage; 
